import { type ComponentProps } from 'react';
import { clamp } from 'es-toolkit';
import { Icon, Loader } from '../../components';
import { cn } from '../../utils';
import { useTranslation } from '../../providers/UIProvider';
import { useRotatingIndex } from './use-rotating-index';
import { TONE_ICON_CLASS } from './tone';
import type { NotificationView } from './types';

type IconName = ComponentProps<typeof Icon>['name'];

/** Ring geometry in viewBox units. */
const STROKE = 5;
const RADIUS = (36 - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/** Circular progress indicator with the percentage in the middle, starting at 12 o'clock. */
function ProgressRing({ value, label }: { value: number; label: string }) {
  const clamped = clamp(value, 0, 100);
  const percent = Math.round(clamped);

  return (
    <svg
      role='progressbar'
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
      viewBox='0 0 36 36'
      fill='none'
      className='size-10 shrink-0'
    >
      {/* Rotated on the group so the percentage below stays upright. */}
      <g transform='rotate(-90 18 18)'>
        <circle cx='18' cy='18' r={RADIUS} strokeWidth={STROKE} className='stroke-border-base' />
        <circle
          cx='18'
          cy='18'
          r={RADIUS}
          strokeWidth={STROKE}
          strokeLinecap='round'
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={CIRCUMFERENCE * (1 - clamped / 100)}
          className='stroke-warn-border transition-[stroke-dashoffset] duration-500 ease-out'
        />
      </g>
      <text
        x='18'
        y='18'
        textAnchor='middle'
        dominantBaseline='central'
        fontSize='10'
        className='fill-warn-border font-semibold'
      >
        {percent}%
      </text>
    </svg>
  );
}

interface ActivityWidgetProps {
  activity: NotificationView;
  className?: string;
  /** Desyncs the rotating label across concurrent widgets — pass the notification id. */
  seedKey?: string;
}

/** One notification row: icon + document name + a (rotating) status line + a live progress ring. */
export function ActivityWidget({ activity, className, seedKey }: ActivityWidgetProps) {
  const t = useTranslation('notifications');
  const rotate = useRotatingIndex(
    activity.titleKeys.length,
    activity.titleKeys.length > 1,
    seedKey
  );
  const key = activity.titleKeys[rotate % activity.titleKeys.length] ?? activity.titleKeys[0];
  const status = t(key, activity.titleParams ?? undefined);
  const name = activity.titleParams?.name;

  return (
    <div className={cn('flex items-center gap-md', className)}>
      <span
        className={cn(
          'flex size-9 shrink-0 items-center justify-center rounded-full',
          TONE_ICON_CLASS[activity.tone]
        )}
      >
        {/* Backend emits names from the shared ui-toolkit icon set. */}
        <Icon name={activity.icon as IconName} size='sm' />
      </span>
      <div className='min-w-0 flex-1'>
        {name != null && (
          <p className='truncate text-sm font-semibold text-foreground-base'>{name}</p>
        )}
        <p
          className={cn(
            'truncate',
            name != null
              ? 'text-xs text-foreground-muted'
              : 'text-sm font-medium text-foreground-base'
          )}
        >
          {status}
        </p>
      </div>
      {activity.progress != null ? (
        <ProgressRing
          value={activity.progress}
          label={name != null ? `${name} — ${status}` : status}
        />
      ) : (
        activity.isBusy && <Loader size='sm' />
      )}
    </div>
  );
}
